import React, { useState, useRef } from 'react';
import { Star, Quote, ThumbsUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { testimonials } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);
  const { t, language } = useLanguage();

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const cardWidth = 400;
      const scrollAmount = direction === 'left' ? -cardWidth : cardWidth;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      
      if (direction === 'left' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1);
      } else if (direction === 'right' && currentIndex < testimonials.length - 1) {
        setCurrentIndex(currentIndex + 1);
      }
    }
  };

  return (
    <section id="avis" className="py-24 bg-[#050505] relative overflow-hidden">
      {/* Animated background gradients */}
      <div className="absolute top-1/4 right-0 w-1/2 h-1/2 bg-gradient-to-l from-[#00D9FF]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '5s' }} />
      <div className="absolute bottom-1/4 left-0 w-1/2 h-1/2 bg-gradient-to-r from-[#FF1493]/10 to-transparent blur-3xl animate-pulse" style={{ animationDuration: '6s', animationDelay: '1s' }} />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in">
          <div className="inline-block mb-6">
            <div className="flex items-center gap-3 px-6 py-3 rounded-full glass-effect border border-white/20 backdrop-blur-xl">
              <ThumbsUp className="w-5 h-5 text-[#00D9FF] animate-pulse" />
              <span className="text-sm font-bold gradient-text tracking-widest">{t.reviews.badge}</span>
            </div>
          </div>
          <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 animate-slide-in-left" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {t.reviews.title} <span className="gradient-text">{t.reviews.titleAccent}</span>
          </h2>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto leading-relaxed animate-slide-in-right" style={{ animationDelay: '0.2s' }}>
            {t.reviews.subtitle}
          </p>

          {/* Rating summary */}
          <div className="flex items-center justify-center gap-3 mt-8">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 fill-[#fad24b] text-[#fad24b]" />
              ))}
            </div>
            <span className="text-white text-2xl font-bold">4.9</span>
            <span className="text-gray-400">Google Reviews (129)</span>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative max-w-7xl mx-auto">
          {/* Navigation Buttons */}
          <button
            onClick={() => scroll('left')}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full glass-effect border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-all duration-300 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed -translate-x-6"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>

          <button
            onClick={() => scroll('right')}
            disabled={currentIndex >= testimonials.length - 3}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full glass-effect border border-white/20 flex items-center justify-center text-white hover:bg-white/10 transition-all duration-300 hover:scale-110 disabled:opacity-30 disabled:cursor-not-allowed translate-x-6"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          {/* Scrollable Container */}
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide scroll-smooth px-4"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            {testimonials.map((testimonial) => (
              <Card
                key={testimonial.id}
                className="group relative bg-[#121212] border-white/10 hover:border-[#00D9FF]/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-[#00D9FF]/30 flex-shrink-0 w-[380px]"
              >
                {/* Animated gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#00D9FF]/0 via-[#5B7FFF]/0 to-[#FF1493]/0 group-hover:from-[#00D9FF]/10 group-hover:via-[#5B7FFF]/5 group-hover:to-[#FF1493]/10 transition-all duration-700" />
                
                {/* Animated border glow */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#00D9FF] to-transparent animate-shimmer" />
                </div>

                {/* Quote icon */}
                <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                  <Quote className="w-12 h-12 text-[#00D9FF]" />
                </div>

                <CardContent className="p-6 relative z-10">
                  {/* Rating stars */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#fad24b] text-[#fad24b]" />
                    ))}
                  </div>

                  {/* Comment */}
                  <p className="text-gray-300 text-sm leading-relaxed mb-6 group-hover:text-white transition-colors duration-500">
                    "{testimonial.comment}"
                  </p>

                  {/* Separator */}
                  <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent mb-4" />

                  {/* Author info with avatar */}
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.avatar} 
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full"
                    />
                    <div>
                      <p className="text-white font-semibold group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#00D9FF] group-hover:to-[#FF1493] transition-all duration-500">
                        {testimonial.name}
                      </p>
                      <p className="text-gray-400 text-xs">{language === 'en' ? testimonial.dateEn : testimonial.date}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Scroll indicator dots */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(Math.ceil(testimonials.length / 3))].map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentIndex(i * 3);
                  if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTo({ left: i * 3 * 400, behavior: 'smooth' });
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  Math.floor(currentIndex / 3) === i
                    ? 'bg-[#00D9FF] w-8'
                    : 'bg-white/30 hover:bg-white/50'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-12 animate-fade-in" style={{ animationDelay: '1s' }}>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 px-6 py-3 glass-effect border border-white/10 rounded-full">
              <Star className="w-5 h-5 fill-[#fad24b] text-[#fad24b]" />
              <span className="text-white font-semibold">{t.reviews.rating}</span>
            </div>
            <div className="flex items-center gap-2 px-6 py-3 glass-effect border border-white/10 rounded-full">
              <ThumbsUp className="w-5 h-5 text-[#00D9FF]" />
              <span className="text-white font-semibold">{t.reviews.satisfied}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Hide scrollbar CSS */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </section>
  );
};
